import { useAppDispatch, useAppSelector } from '@/shared/hooks/useStore';
import { selectFormState, setField } from '@/viewmodel/shared/form/state/formSlice';
import type {
  DynamicFormView,
  FormFieldConfigProps,
  OnSubmitFunction,
} from '@/viewmodel/shared/form/types';
import { useEffect, useMemo, useRef } from 'react';

interface DynamicFormInput {
  readonly fields: FormFieldConfigProps[];
  readonly initialValues?: Record<string, string> | undefined;
  readonly clearOnUnmount?: boolean | undefined;
  readonly onSubmit?: OnSubmitFunction | undefined;
}

const isInput = (field: FormFieldConfigProps): boolean => field.type !== 'divider';

const toFormString = (value: unknown): string =>
  typeof value === 'string' || typeof value === 'number' ? String(value || '') : '';

export function useDynamicFormViewModel({
  fields,
  initialValues = {},
  clearOnUnmount = true,
  onSubmit,
}: DynamicFormInput): DynamicFormView {
  const dispatch = useAppDispatch();
  const formState = useAppSelector(selectFormState);

  const prevInitialValuesRef = useRef<Record<string, string>>(initialValues);
  const prevStrRef = useRef<string>(JSON.stringify(initialValues));
  const fieldsRef = useRef(fields);
  const formStateRef = useRef(formState);
  formStateRef.current = formState;

  const memoizedInitialValues = useMemo(() => {
    const currentStr = JSON.stringify(initialValues);
    if (currentStr !== prevStrRef.current) {
      prevStrRef.current = currentStr;
      prevInitialValuesRef.current = initialValues;
    }
    return prevInitialValuesRef.current;
  }, [initialValues]);

  useEffect(() => {
    for (const field of fieldsRef.current) {
      if (!isInput(field)) continue;
      const { fieldName } = field;
      if (formStateRef.current[fieldName]) continue;
      const initialValue = memoizedInitialValues[fieldName] || '';
      dispatch(
        setField({ fieldName, value: initialValue, isValid: initialValue === '' ? null : true }),
      );
    }
  }, [memoizedInitialValues, dispatch]);

  useEffect(() => {
    return () => {
      if (!clearOnUnmount) return;
      for (const field of fieldsRef.current) {
        if (!isInput(field)) continue;
        dispatch(setField({ fieldName: field.fieldName, value: '', isValid: null }));
      }
    };
  }, [clearOnUnmount, dispatch]);

  const rawValueOf = (fieldName: string): string | number => {
    const value = formState[fieldName]?.value;
    return typeof value === 'string' || typeof value === 'number' ? value : '';
  };

  return {
    fields,
    isFormValid: fields.filter(isInput).every((f) => formState[f.fieldName]?.isValid === true),
    rawValueOf,
    stringValueOf: (fieldName: string): string => {
      const value = formState[fieldName]?.value;
      return typeof value === 'string' ? value : '';
    },
    isInvalid: (fieldName: string): boolean => formState[fieldName]?.isValid === false,
    setValue: (fieldName: string, value: string | number, isValid: boolean): void => {
      dispatch(setField({ fieldName, value, isValid }));
    },
    fieldOf: (fieldName: string, regex: RegExp) => {
      const field = formState[fieldName];
      return {
        value: typeof field?.value === 'string' ? field.value : '',
        isValid: field?.isValid ?? null,
        errorMessage: field?.errorMessage ?? undefined,
        change: (value: string): void => {
          dispatch(setField({ fieldName, value, isValid: regex.test(value) }));
        },
      };
    },
    submit: async (): Promise<void> => {
      const formData: Record<string, string> = {};
      for (const field of fields.filter(isInput)) {
        formData[field.fieldName] = toFormString(formState[field.fieldName]?.value);
      }
      if (onSubmit) await onSubmit(formData);
    },
  };
}
